import { useFormik } from 'formik'
import { SignUpUsernameForm as Form } from 'ui-components'
import * as yup from 'yup'

import { useRouter } from 'next/router'
import { isJoinWorkspaceLink } from 'user-auth/helpers/isJoinWorkspaceLink'
import { useAuth } from 'user-auth/hooks/useAuth'
import { useUserExistsMutation } from 'user-auth/hooks/useUserExistsMutation'
import { useAtom } from 'user-auth/libs/jotai'
import { atoms } from 'user-auth/states/form'
import { postSignInRedirect } from 'user-auth/states/postSignInRedirect'
import { getJoinWorkspaceLink } from 'workspace-member-creator/helpers/getJoinWorkspaceLink'
import { useVerifyCaptchaToken } from 'user-auth'
import { useState } from 'react'

const schema = yup.object().shape({
  email: yup.string().email().required('Invalid email address')
})

export function UsernameForm(props) {
  const { slotProps } = props

  const { authenticated } = useAuth()

  const router = useRouter()
  const { workspaceId, workspaceName, inviteId, redirect } = router.query

  const [, setEmail] = useAtom(atoms.otp.email)
  const [, setForm] = useAtom(atoms.form.signUp)

  const { mutateAsync: userExists } = useUserExistsMutation()
  const { verifyToken } = useVerifyCaptchaToken()

  const go = (pathname) => {
    window.location.href = pathname
  }

  const form = useFormik({
    validationSchema: schema,
    initialValues: {
      email: '',
      // this is used to check if the user exists
      userExists: undefined
    },
    validateOnMount: true,
    onSubmit: async (values, helpers) => {
      const { email } = values
      setEmail(email)

      const exists = await userExists({ email })

      if (exists) {
        helpers.setFieldError(
          'userExists',
          'The user with this email address already exists. Please try signing in.'
        )
        return
      }

      if (isJoinWorkspaceLink(router.pathname)) {
        postSignInRedirect.save(
          getJoinWorkspaceLink({
            workspaceId,
            workspaceName,
            inviteId,
            redirect
          })
        )
      }

      if (authenticated) {
        go('/sign-in/callback')
        return
      }

      setForm('createAccount')
    }
  })

  const [enableButton, setEnableButton] = useState(false)

  const handleClick = () => {
    router.push('/sign-in')
  }

  return (
    <Form
      slotProps={{
        Form: {
          component: 'form',
          onSubmit: form.handleSubmit
        },
        EmailInput: {
          ...form.getFieldProps('email'),
          error: form.touched.email && Boolean(form.errors.userExists),
          helperText: form.touched.email && form.errors.userExists
        },
        Button: {
          type: 'submit',
          loading: form.isSubmitting,
          disabled: !enableButton
        },
        SignInLink: {
          onClick: handleClick,
          ...slotProps?.SignInLink
        },
        TurnstileWidget: {
          handleVerifyToken: async (token: string) => {
            const isValid = await verifyToken(token)
            setEnableButton(isValid)
            return isValid
          },
          showCaptcha: form.isValid
        }
      }}
    />
  )
}
